import { Injectable, inject } from '@angular/core';
import { Storage, ref, getDownloadURL } from '@angular/fire/storage';
import { Observable, from, of } from 'rxjs';

/**
 * Request files are stored relative to the request so retention rules can purge a
 * whole folder at once:
 *
 *   requests/{requestId}/attachments/   <- purged early (privacy: photos of a private space)
 *   requests/{requestId}/invoices/      <- kept for the accounting compliance period
 *
 * Firestore documents carry the relative `storagePath`; the download URL is rebuilt at
 * read time. `@angular/fire` binds Storage to the environment's bucket during app
 * startup, so the relative path alone is enough — no project id is baked into the data.
 *
 * Legacy documents only have an absolute `downloadUrl` / `invoice` URL. Every resolver
 * here falls back to it, so the app works before and after the migration runs.
 */
@Injectable({ providedIn: 'root' })
export class RequestStorageService {
  private storage = inject(Storage);

  attachmentsFolder(requestId: string): string {
    return `requests/${requestId}/attachments`;
  }

  invoicesFolder(requestId: string): string {
    return `requests/${requestId}/invoices`;
  }

  attachmentPath(requestId: string, fileName: string): string {
    return `${this.attachmentsFolder(requestId)}/${fileName}`;
  }

  invoicePath(requestId: string, fileName: string): string {
    return `${this.invoicesFolder(requestId)}/${fileName}`;
  }

  /**
   * Builds a download URL for a stored file, preferring the request-relative path.
   *
   * @param storagePath relative path, e.g. `requests/{id}/attachments/photo.jpg`
   * @param legacyUrl absolute URL kept on pre-migration documents
   */
  async resolveUrl(
    storagePath?: string | null,
    legacyUrl?: string | null
  ): Promise<string> {
    const path = (storagePath ?? '').trim();
    const fallback = (legacyUrl ?? '').trim();

    if (!path) {
      return fallback;
    }

    // A document written before the migration may hold an absolute URL here.
    if (this.isAbsoluteUrl(path)) {
      return path;
    }

    try {
      return await getDownloadURL(ref(this.storage, path.replace(/^\/+/, '')));
    } catch (error) {
      console.warn(`Could not resolve storage path "${path}", using stored URL`, error);
      return fallback;
    }
  }

  resolveUrl$(
    storagePath?: string | null,
    legacyUrl?: string | null
  ): Observable<string> {
    const path = (storagePath ?? '').trim();
    if (!path || this.isAbsoluteUrl(path)) {
      return of(path || (legacyUrl ?? '').trim());
    }
    return from(this.resolveUrl(storagePath, legacyUrl));
  }

  private isAbsoluteUrl(value: string): boolean {
    return (
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('gs://')
    );
  }
}
